import type { MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'
import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView } from './view/accommodation'
import { OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR_V2, OFFER_TYPE_VILLA } from 'constants/offer'
import config from 'constants/config'
import { getCustomOfferItems } from './view/customOffer'
import { getVillaViews } from './view/villa'
import { getBundleAndSaveItems } from './view/bundleAndSave'
import { isCruiseOfferView } from 'checkout/lib/utils/cruises/view'
import { getCarHireItemsView } from './view/carHire'
import { cartIncludesFlights, getFlightItems, getFlightItemsView, isStandaloneFlights } from './view/flights'
import { getGiftCardItems } from './view/giftCard'
import { getPurchaseCreditItems } from './view/purchaseCredit'
import isInsuranceSupported from 'checkout/selectors/request/isInsuranceSupported'
import isPassportCardInsuranceSupportedViaCampaign from 'checkout/selectors/request/isPassportCardInsuranceSupportedViaCampaign'
import isPassportCardInsuranceSupportedForToursCruises from 'checkout/selectors/request/isPassportCardInsuranceSupportedForToursCruises'
import isPassportCardInsuranceSupportedPostPurchase from 'checkout/selectors/request/isPassportCardInsuranceSupportedPostPurchase'
import { shouldShowArrivalDetailsForm, shouldShowPaymentForm, shouldShowTravellerForm } from './stepsSelectors'
import { isStandaloneLoyaltyPurchasePoints } from './view/loyaltyPurchasePoints'
import { isBalancePending } from 'lib/payment/depositsUtils'
import {
  CHECKOUT_INSURANCE_CONTAINER_ID,
  CHECKOUT_EXPERIENCE_TRANSFER_CONTAINER_ID,
  CHECKOUT_REVIEW_CONTAINER_ID,
  CHECKOUT_TRAVELLER_CONTAINER_ID,
  CHECKOUT_ARRIVAL_DETAILS_CONTAINER_ID,
  CHECKOUT_PAYMENTS_CONTAINER_ID,
  CHECKOUT_BUNDLED_CAR_HIRE_CONTAINER_ID,
  CHECKOUT_MERCHANDISE_CONTAINER_ID,
  CHECKOUT_LOYALTY_PURCHASE_POINTS_CONTAINER_ID,
  CHECKOUT_EXPERIENCE_LOUNGE_CONTAINER_ID,
  CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2,
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
  CHECKOUT_ITEM_TYPE_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_FLIGHT,
  CHECKOUT_ITEM_TYPE_INSURANCE,
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION,
  CHECKOUT_ITEM_TYPE_TRANSFER,
} from 'constants/checkout'
import { getIsLuxPlusEnabled, getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isStandaloneLuxPlusSubscription } from './view/luxPlusSubscription'
import getTourV2ExperienceItemsViews from 'checkout/selectors/view/itemViews/getTourV2ExperienceItemsViews'
import { isTravelProtectionAvailableInRegion } from 'selectors/featuresSelectors'
import { isSpoofed } from 'selectors/isSpoofed'
import { isSimpleStandaloneCheckout, isStandaloneExperience, getCartId, selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { INSURANCE_TITLE_DESCRIPTOR, TRAVEL_PROTECTION_TITLE_DESCRIPTOR } from 'checkout/constants/insurance'
import { getExperienceOneClickTransferOptions, getFlightLoungePassExperienceAvailable, getOneClickTransferPresentationData, getIsOneClickTransferModuleEnabled, getIsOneClickTransferAvailable, getIsOneClickTransferLoading, getBundledTransferDirection, getHasBothTransferDirectionsBundled, getHasOneClickTransferOptionsWithKnownType } from './experienceSelectors'
import { findPostPurchaseCheckout, isCarHireItem, isPassportCardInsuranceItem } from 'lib/checkout/checkoutUtils'
import isGroupPaymentShareCheckout from 'checkout/selectors/payment/groupPayment/isGroupPaymentShareCheckout'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import getCheckoutTotal from 'checkout/selectors/payment/getCheckoutTotal'
import { isCruiseCheckout } from './view/cruise'
import { isBundledCarHireEnabled, isCartEligibleForBundledCarHire } from 'selectors/carHireSelectors'
import getExperienceItemsView from './view/itemViews/getExperienceItemsView'
import getTransferItems from './view/items/getTransferItems'
import { isFlightWithEmbeddedInsurance } from 'checkout/utils'
import getMerchandiseItems from './view/items/getMerchandiseItems'
import getLoyaltyPurchasePointsItems from './view/items/getLoyaltyPurchasePointsItems'
import { getCheckoutBenefitsView } from 'luxPlus/selectors/checkout/benefits'
import getCanRedeemCheckoutLoyaltyBenefits from './loyalty/getCanRedeemCheckoutLoyaltyBenefits'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL } from 'loyalty/constants/loyaltyBonusPoints'
import { getLoyaltyBonusPointsCampaignTypes } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import { capitaliseAll } from 'lib/string/stringUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getUnifiedTransferView from './view/itemViews/getUnifiedTransferView'
import { getExperimentEnabledDecision, getOptimizelyExperimentVariation, isAllowedToTriggerExperiment } from 'lib/optimizely/optimizelyUtils'
import { ChertoExperiments, Experiments } from 'constants/experiments'
import { getInsuranceItems } from './view/insurance'
import { getFunnelFeatures } from './funnel'
import { getIsInternationalCheckoutFlowTreatment } from './internationalCheckoutFlow'
import { isLuxPlusAvailableToAddToCart } from './navigationSelectors'
import {
  checkoutShouldShowBookingProtection,
  getShouldShowBookingProtectionSection,
  getShouldShowRegularInsuranceSection,
  getShouldShowSideBySideInsuranceSection,
  getShouldShowSpoofedInsuranceSection,
  isLeLiveUtmMedium,
  shouldShowSpoofedInsuranceSectionForInputs,
} from './checkoutInsuranceSectionSelectors'
import { hasBundledAccommodationRate } from './hasBundledAccommodationRate'
import isCheckoutEsimUpsellExperienceItem from 'loyalty/lib/esims/esimCheckoutUtils'

export {
  checkoutShouldShowBookingProtection,
  getShouldShowBookingProtectionSection,
  getShouldShowRegularInsuranceSection,
  getShouldShowSideBySideInsuranceSection,
  getShouldShowSpoofedInsuranceSection,
  hasBundledAccommodationRate,
  isLeLiveUtmMedium,
  shouldShowSpoofedInsuranceSectionForInputs,
}
const sectionTitleMessages = defineMessages({
  reviewGift: {
    id: 'ecGGgBWnEDdTNfNh1GEL',
    defaultMessage: 'Let’s review your gift',
    description: 'Checkout review section title when the order is a gift',
  },
  reviewAndPay: {
    id: 'gQEaHDKGcuGHj8cSea4N',
    defaultMessage: 'Review & Pay',
    description: 'Single checkout step label when reviewing and paying in checkout',
  },
  reviewExperience: {
    id: 'hRQBXm0AhOY5-Yj24_Un',
    defaultMessage: 'Review your experience',
    description: 'Checkout review section title for standalone experience bookings',
  },
  reviewDefault: {
    id: 'Q4Z_iyuSeNzj0WYDrpqX',
    defaultMessage: 'Let’s review your escape',
    description: 'Default checkout review section title for travel bookings',
  },
  compactReviewAndPay: {
    id: 'WWUyZhPhn9KuK7VTDv4c',
    defaultMessage: 'Review & pay',
    description: 'Checkout review page title when the compact item components experiment is on',
  },
  travellerCarHireDriver: {
    id: 'lolU7Es0iRykjHLUK8PN',
    defaultMessage: 'Who’s the driver?',
    description: 'Checkout traveller section title when booking is car hire only',
  },
  travellerDefault: {
    id: 'M44ETbZasQKv7SJGovxH',
    defaultMessage: 'Who’s going?',
    description: 'Default checkout traveller section title during checkout',
  },
  compactTravellers: {
    id: 'zKGwYB3NyrFLg-PzIjCk',
    defaultMessage: 'Travellers',
    description: 'Checkout traveller section title when the compact item components experiment is on',
  },
  insuranceProtectFlights: {
    id: '9Q6clUe2_OG2ORoErAu4',
    defaultMessage: 'Protect yourself if you need to cancel your flights',
    description: 'Checkout insurance section title for standalone flight bookings',
  },
  insuranceFlightsProtectedCoverHotel: {
    id: '3QckaJ8TDYHURVos6fIo',
    defaultMessage: 'Your flights are protected — now cover your hotel too',
    description: 'Checkout insurance section title when all flights are protected but hotel is not',
  },
  insuranceSecureFullProtection: {
    id: '4Yj3mnqZxeIj769Xydgc',
    defaultMessage: 'Secure full protection — flights and hotel covered if plans change',
    description: 'Checkout insurance section title when partial flight protection exists',
  },
  insuranceFlightsAndAccommodationProtected: {
    id: 'Sx4w4JfrHPTvzfUCtUaz',
    defaultMessage: 'Your flights and accommodation are protected — secure full booking protection too',
    description: 'Checkout insurance section title when flights and accommodation are both protected',
  },
  insuranceHotelProtectedCoverFlights: {
    id: 'C-GMNykP00o-9IpKmQRV',
    defaultMessage: 'Your hotel is protected — now cover your flights too',
    description: 'Checkout insurance section title when hotel is protected but flights are not',
  },
  insuranceHotelProtectedSecureFull: {
    id: 'gWXcnvlWEDyKSlda-A_q',
    defaultMessage: 'Your hotel is protected — secure full protection',
    description: 'Checkout insurance section title when only hotel is protected and no flights in booking',
  },
  insuranceProtectBooking: {
    id: '9XrA6Zix7O3fqAZh27WG',
    defaultMessage: 'Protect yourself if you need to cancel your booking',
    description: 'Default checkout insurance section title for booking protection',
  },
  insuranceSideBySide: {
    id: 'IaaFXopT1Ci6NHCdtcKX',
    defaultMessage: 'Protect your trip',
    description: 'Checkout insurance section title when cancellation protection and travel protection are offered side by side',
  },
  insuranceCruise: {
    id: 'kCE5pc6GE7RYtRXxL97o',
    defaultMessage: 'Want to add cruise protection?',
    description: 'Checkout insurance section title for cruise bookings',
  },
  passportCardInsurancePostPurchase: {
    id: 'Utp9GOsiWR0YlqPYG_0h',
    defaultMessage: 'Travel Insurance',
    description: 'Checkout insurance section title when adding PassportCard insurance to an existing order',
  },
  passportCardInsurance: {
    id: 'NJPV7fFUvLI_ehJEKWX5',
    defaultMessage: 'PassportCard Comprehensive Travel Insurance',
    description: 'Checkout insurance section title for the exclusive PassportCard insurance checkout section',
  },
  bundledCarHireDiscount: {
    id: 't7nNwgKfzkVbRNSni3lu',
    defaultMessage: 'Unlock {discountPercentage}% off car hire for your stay',
    description: 'Checkout bundled car hire section title with discount percentage',
  },
  bundledCarHireExplore: {
    id: 'jVPC45OeouMBSZ90oZ1i',
    defaultMessage: 'Explore exclusive Car Hire offers for your stay',
    description: 'Checkout bundled car hire section title for objective intent messaging',
  },
  bundledCarHireLoyalty: {
    id: 'g9n_akIG3EGhtmnfMzNH',
    defaultMessage: 'Get exclusive {campaignLabel} offers on car hire for your stay',
    description: 'Checkout bundled car hire section title with loyalty bonus points campaign label',
  },
  experienceTransfer: {
    id: '8W2hnF9tqoT8iFsvZSrm',
    defaultMessage: 'Need airport transfers?',
    description: 'Checkout experience transfer section title during checkout',
  },
  experienceTransferToHotel: {
    id: 'O9dKREPkdBTkLVfiNcuP',
    defaultMessage: 'Add your airport to hotel transfer',
    description: 'Checkout experience transfer section title when bundle already covers the airport-bound direction',
  },
  experienceTransferToAirport: {
    id: 'O5QPHaWGBjrOM9kyW7Oc',
    defaultMessage: 'Add your return to the airport transfer',
    description: 'Checkout experience transfer section title when bundle already covers the hotel-bound direction',
  },
  experienceLounge: {
    id: 'uX71jW6xpdlPxHrTOrwM',
    defaultMessage: 'Turn wait time into downtime',
    description: 'Checkout experience lounge section title during checkout',
  },
  arrivalDetails: {
    id: 'tOvUmiZ7GH9XDj9SmYFu',
    defaultMessage: 'When will you be arriving?',
    description: 'Checkout arrival details section title during the checkout and payment process',
  },
  paymentHow: {
    id: 'IEmfEWYQqPYFbvKFAFlL',
    defaultMessage: 'How would you like to pay?',
    description: 'Checkout payment section title during the checkout and payment process',
  },
  merchandiseReview: {
    id: '93Jj2baqfsLcDgCPdjWM',
    defaultMessage: 'Let’s review your shop order',
    description: 'Checkout merchandise section title during the checkout and payment process',
  },
  loyaltyPurchasePointsReview: {
    id: 'hktd9KOU1XqHGUdaJmZs',
    defaultMessage: 'Points to purchase',
    description: 'Checkout review section title for buying additional Société loyalty points',
  },
})

/**
 * `mobilePlacement: 'header'` moves the title into the sticky nav header on mobile, where the
 * design centres it in place of the logo, and keeps it in the body from tablet up.
 */
export type CheckoutSectionTitle = {
  mobilePlacement?: 'header'
  headingAs?: 'h2' | 'h4'
  headingVariant?: 'heading2' | 'heading3'
} & (
  | { kind: 'descriptor'; descriptor: MessageDescriptor; values?: Record<string, string | number> }
  | { kind: 'literal'; text: string }
)

export interface CheckoutNumberedSection<T> {
  id: string
  title?: CheckoutSectionTitle
  checkoutNumber?: string
  data: T
}

export type CheckoutSectionsReview = 'customOffer' | 'accommodation' | 'villa' | 'bundleAndSave' | 'cruise' | 'carHire' | 'tour' | 'tourExperience' | 'flight' | 'flightDateChange' | 'experience' | 'reviewTransfer' | 'giftCard' | 'purchaseCredit' | 'customerServicePanel' | 'luxPlusInlineToggle' | 'luxPlusInflow' | 'luxPlusReconsider' | 'luxPlusStandalone' | 'loyaltyBenefits' | 'loyalty' | 'charterUpgrade' | 'flightSeats' | 'flightBaggage' | 'cruiseAmendPassengers'
export type CheckoutSectionsExperienceTransfer = 'experienceTransfer'
export type CheckoutSectionsExperienceLounge = 'experienceLounge'
export type CheckoutSectionsInsurance = 'insurance' | 'bookingProtection' | 'sideBySideInsurance' | 'spoofedInsurance' | 'passportCardInsurance'
export type CheckoutSectionsCarHire = 'bundledCarHire'
export type CheckoutSectionsTravellerDetails = 'customOfferTravellers' | 'travellerDetails' | 'flightServices' | 'carHireArrivalFlightNumber' | 'agentBookingDetails' | 'depositDetailsNotice' | 'paymentScheduleDetailsNotice'
export type CheckoutSectionsArrivalDetails = 'arrivalDetails'
export type CheckoutSectionsHighValue = 'loyalty'
export type CheckoutSectionsPayment = 'instalmentBalancePaymentSchedule' | 'paymentForm'
export type CheckoutSectionsMerchandise = 'merchandise' | 'merchandiseShipping'
export type CheckoutSectionsLoyaltyPurchasePoints = 'loyaltyPurchasePoints'

export interface CheckoutSections {
  review: CheckoutNumberedSection<Array<CheckoutSectionsReview>>
  experienceTransfer: CheckoutNumberedSection<Array<CheckoutSectionsExperienceTransfer>>
  experienceLounge: CheckoutNumberedSection<Array<CheckoutSectionsExperienceLounge>>
  insurance: CheckoutNumberedSection<Array<CheckoutSectionsInsurance>>
  bundledCarHire: CheckoutNumberedSection<Array<CheckoutSectionsCarHire>>
  travellerDetails: CheckoutNumberedSection<Array<CheckoutSectionsTravellerDetails>>
  arrivalDetails: CheckoutNumberedSection<Array<CheckoutSectionsArrivalDetails>>
  payment: CheckoutNumberedSection<Array<CheckoutSectionsPayment>>
  merchandise: CheckoutNumberedSection<Array<CheckoutSectionsMerchandise>>
  loyaltyPurchasePoints: CheckoutNumberedSection<Array<CheckoutSectionsLoyaltyPurchasePoints>>
}

const activeSections = <T>(sections: Array<[T, boolean]>) => sections.filter(section => Boolean(section[1])).map(section => section[0])

// @TODO: a 'cruise' is not an accommodation item
export const filterCheckoutAccommodationView = (view: App.Checkout.AccommodationOfferView) => {
  return view.offerType !== OFFER_TYPE_CRUISE && view.offerType !== OFFER_TYPE_TOUR_V2 && view.offerType !== OFFER_TYPE_VILLA
}

const accommodationViews = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (offerViews) => offerViews.data.filter(filterCheckoutAccommodationView),
)

const checkoutShouldShowCarHireArrivalInput = createSelector(
  (state: App.State) => getCarHireItemsView(state),
  (carHireViews) => !!carHireViews.data.length && (carHireViews.data[0].item.isPickUpAtAirport ||
    carHireViews.data[0]?.pickUpLocation.name.toUpperCase().includes('AIRPORT')),
)

/**
 * Normal purchase checkout. Post-purchase carts (change dates, balances, upgrades)
 * set a mode outside this set and stay on the control experience.
 */
export function isStandardCheckoutCartMode(mode: App.CheckoutCartMode | undefined): boolean {
  return mode === undefined || mode === 'default' || mode === 'vertical-select'
}

export function getIsStandardCheckoutCart(state: App.State): boolean {
  return isStandardCheckoutCartMode(state.checkout.cart.mode)
}

/**
 * Compact review tiles, plus checkout add-ons that ride along with them.
 * Booking protection stays outside this set. A standalone flight cart may include it
 * without moving hotel or mixed-flight carts into the experiment.
 * Any other cart item keeps the checkout on the control experience.
 */
const COVERED_CHECKOUT_ITEM_COMPONENT_TYPES = new Set<string>([
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2,
  CHECKOUT_ITEM_TYPE_FLIGHT,
  CHECKOUT_ITEM_TYPE_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_INSURANCE,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION,
  CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE,
  CHECKOUT_ITEM_TYPE_TRANSFER,
  'loungePassItem',
  'loyaltyTransferItem',
  'loyaltyEsimItem',
])

const STANDALONE_FLIGHT_COVERED_CHECKOUT_ITEM_COMPONENT_TYPES = new Set<string>([
  ...COVERED_CHECKOUT_ITEM_COMPONENT_TYPES,
  CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
])

export function isCheckoutItemComponentsCartEligible(state: App.State): boolean {
  if (!getIsStandardCheckoutCart(state)) {
    return false
  }

  const coveredTypes = isStandaloneFlights(state)
    ? STANDALONE_FLIGHT_COVERED_CHECKOUT_ITEM_COMPONENT_TYPES
    : COVERED_CHECKOUT_ITEM_COMPONENT_TYPES

  return (state.checkout.cart.items ?? []).every(item => coveredTypes.has(item.itemType))
}

/**
 * Matches `useExperiment(...).isAnyVariant` for this Cherto experiment: `state.cherto`, the
 * `CHERTO_<id>` local override, and the same off/control and metasearch gates as the hook.
 * Treatment is only available on a normal checkout whose items are all covered.
 */
export function isCheckoutItemComponentsVariant(state: App.State): boolean {
  if (!isCheckoutItemComponentsCartEligible(state)) {
    return false
  }

  const experimentId = ChertoExperiments.croCheckoutItemComponents.id

  if (!isAllowedToTriggerExperiment(true, state.utm.content, experimentId)) {
    return false
  }

  const override = config[`CHERTO_${experimentId}`]
  const variationKey = override ?? state.cherto?.chertoExperiments?.[experimentId]?.variationKey

  return getExperimentEnabledDecision(variationKey)
}

export function isCheckoutItemComponentsCompactStay(state: App.State): boolean {
  return isCheckoutItemComponentsVariant(state)
}

export const getReviewSectionTitle = createSelector(
  (state: App.State) => isSimpleStandaloneCheckout(state),
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isCheckoutItemComponentsCompactStay(state),
  (simpleStandaloneCheckout, standaloneExperience, isGift, postPurchase, compactStay): CheckoutSectionTitle | undefined => {
    // The review section title is this checkout's page-title slot, and the ancillaries design puts
    // "Review & Pay" there — the traveller cards carry no heading of their own.
    if (postPurchase === 'flight-seats' || postPurchase === 'flight-baggage') {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewAndPay, mobilePlacement: 'header' }
    }
    if (postPurchase === 'charter-upgrade' || postPurchase === 'cruise-amend-passengers') {
      return undefined
    }
    if (isGift) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewGift }
    } else if (simpleStandaloneCheckout) {
      return undefined
    } else if (standaloneExperience) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewExperience }
    } else if (compactStay) {
      return {
        kind: 'descriptor',
        descriptor: sectionTitleMessages.compactReviewAndPay,
        headingAs: 'h2',
        headingVariant: 'heading2',
      }
    } else {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.reviewDefault }
    }
  },
)

const getTravellerSectionTitle = createSelector(
  (state: App.State) => getCheckoutTravellerSchema(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => isCheckoutItemComponentsCompactStay(state),
  (formSchema, isGift, cartItems, compactStay): CheckoutSectionTitle | undefined => {
    if (cartItems.length > 0 && cartItems.every(isCarHireItem)) return { kind: 'descriptor', descriptor: sectionTitleMessages.travellerCarHireDriver }
    if (isGift) {
      const recipientTitle = formSchema?.accommodationFlightSchema?.properties?.recipient?.title
      if (!recipientTitle) return undefined
      return { kind: 'literal', text: recipientTitle }
    }
    if (compactStay) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.compactTravellers }
    }
    return { kind: 'descriptor', descriptor: sectionTitleMessages.travellerDefault }
  },
)

const getInsuranceSectionTitle = createSelector(
  (state: App.State) => checkoutShouldShowBookingProtection(state),
  (state: App.State) => isInsuranceSupported(state),
  (state: App.State) => getShouldShowSideBySideInsuranceSection(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isTravelProtectionAvailableInRegion(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => isPassportCardInsuranceSupportedViaCampaign(state),
  (state: App.State) => isPassportCardInsuranceSupportedForToursCruises(state),
  (state: App.State) => isPassportCardInsuranceSupportedPostPurchase(state),
  (
    showBookingProtection,
    isInsuranceSupported,
    showSideBySideInsurance,
    isSpoofed,
    standaloneFlights,
    isTravelProtection,
    hasCruiseItems,
    flights,
    insuranceItems,
    postPurchase,
    checkoutItems,
    showPassportCardViaCampaign,
    showPassportCardForToursCruises,
    showPassportCardPostPurchase,
  ): CheckoutSectionTitle | undefined => {
    if (isSpoofed) return undefined

    // Post-purchase carts title the section by what is being added; the buy-with-booking
    // entry points keep the full product name.
    if (shouldShowPassportCardInsuranceSectionForInputs(postPurchase, checkoutItems) || showPassportCardPostPurchase) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.passportCardInsurancePostPurchase }
    }
    if (showPassportCardViaCampaign || showPassportCardForToursCruises) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.passportCardInsurance }
    }

    if (showSideBySideInsurance) {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceSideBySide }
    }

    // Booking protection flow - provides contextual messaging based on existing coverage
    if (showBookingProtection) {
      // Simple case: standalone flights only
      if (standaloneFlights) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceProtectFlights }
      }

      // Analyze existing protection coverage for mixed bookings
      const flightItem = flights[0]
      const securedFlights = flightItem?.flights.filter(isFlightWithEmbeddedInsurance)

      const insuranceItem = insuranceItems[0]
      // Check if accommodation is already protected (via CFMR flex package)
      const isAccommodationProtected = !!insuranceItem?.bedbankRoomIds?.length || !!insuranceItem?.isAccommodationBundle

      // Case 1: Some/all flights protected, but accommodation is not
      if (securedFlights?.length && !isAccommodationProtected) {
        return securedFlights.length === flightItem.flights.length
          ? { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceFlightsProtectedCoverHotel }
          : { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceSecureFullProtection }
      }

      // Case 2: Some/all flights protected AND accommodation is protected
      if (securedFlights?.length && isAccommodationProtected) {
        return securedFlights.length === flightItem.flights.length
          ? { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceFlightsAndAccommodationProtected }
          : { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceSecureFullProtection }
      }

      // Case 3: Accommodation protected but flights are not (and flights exist in booking)
      if (isAccommodationProtected && !securedFlights?.length && !!flightItem) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceHotelProtectedCoverFlights }
      }

      // Case 4: Only accommodation is protected (no flights in booking)
      if (isAccommodationProtected) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceHotelProtectedSecureFull }
      }

      // Default case: no existing protection
      return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceProtectBooking }
    }
    if (isInsuranceSupported && hasCruiseItems) return { kind: 'descriptor', descriptor: sectionTitleMessages.insuranceCruise }
    if (isInsuranceSupported) return { kind: 'descriptor', descriptor: isTravelProtection ? TRAVEL_PROTECTION_TITLE_DESCRIPTOR : INSURANCE_TITLE_DESCRIPTOR }

    return undefined
  },
)

const getBundledCarHireSectionTitle = createSelector(
  (state: App.State) => state.carHire.bundleEligibilityCartList[getCartId(state)],
  (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.carHireObjectiveIntentMessage.id),
  (bundleEligibilityForCart, isObjectiveIntentMessage): CheckoutSectionTitle | undefined => {
    if (!bundleEligibilityForCart?.isEligible) return undefined
    if (bundleEligibilityForCart.benefits?.has('discount')) {
      return {
        kind: 'descriptor',
        descriptor: sectionTitleMessages.bundledCarHireDiscount,
        values: { discountPercentage: bundleEligibilityForCart.discountPercentage ?? 0 },
      }
    }
    if (bundleEligibilityForCart.benefits?.has('loyalty_bonus_points')) {
      const bonusPointsCampaigns = getLoyaltyBonusPointsCampaignTypes({
        bonusPoints: bundleEligibilityForCart.loyaltyBonusPointsMultiplier ?? 0,
        basePoints: 1,
        bonusStatusCredits: 0,
        statusCredits: 0,
      })
      const bonusPointsCampaign = bonusPointsCampaigns[0]
      if (!bonusPointsCampaign) return undefined
      const bonusPointsCampaignLabel = getCurrentIntl().formatMessage(LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[bonusPointsCampaign])

      if (isObjectiveIntentMessage) {
        return { kind: 'descriptor', descriptor: sectionTitleMessages.bundledCarHireExplore }
      }
      return {
        kind: 'descriptor',
        descriptor: sectionTitleMessages.bundledCarHireLoyalty,
        values: { campaignLabel: capitaliseAll(bonusPointsCampaignLabel) },
      }
    }
    return undefined
  },
)

function getNumberedSections({
  reviewSections,
  experienceTransferSection,
  experienceLoungeSection,
  insuranceSections,
  bundledCarHireSection,
  travellerSections,
  arrivalDetails,
  paymentSections,
  merchandiseSections,
  loyaltyPurchasePointsSections,
}: {
  reviewSections: Array<CheckoutSectionsReview>
  insuranceSections: Array<CheckoutSectionsInsurance>
  bundledCarHireSection: Array<CheckoutSectionsCarHire>
  travellerSections: Array<CheckoutSectionsTravellerDetails>
  arrivalDetails: Array<CheckoutSectionsArrivalDetails>
  paymentSections: Array<CheckoutSectionsPayment>
  experienceTransferSection: Array<CheckoutSectionsExperienceTransfer>
  experienceLoungeSection: Array<CheckoutSectionsExperienceLounge>
  merchandiseSections: Array<CheckoutSectionsMerchandise>
  loyaltyPurchasePointsSections: Array<CheckoutSectionsLoyaltyPurchasePoints>
}): CheckoutSections {
  const sections = [
    { name: 'review', id: CHECKOUT_REVIEW_CONTAINER_ID, data: reviewSections },
    { name: 'experienceTransfer', id: CHECKOUT_EXPERIENCE_TRANSFER_CONTAINER_ID, data: experienceTransferSection },
    { name: 'experienceLounge', id: CHECKOUT_EXPERIENCE_LOUNGE_CONTAINER_ID, data: experienceLoungeSection },
    { name: 'insurance', id: CHECKOUT_INSURANCE_CONTAINER_ID, data: insuranceSections },
    { name: 'bundledCarHire', id: CHECKOUT_BUNDLED_CAR_HIRE_CONTAINER_ID, data: bundledCarHireSection },
    { name: 'travellerDetails', id: CHECKOUT_TRAVELLER_CONTAINER_ID, data: travellerSections },
    { name: 'arrivalDetails', id: CHECKOUT_ARRIVAL_DETAILS_CONTAINER_ID, data: arrivalDetails },
    { name: 'payment', id: CHECKOUT_PAYMENTS_CONTAINER_ID, data: paymentSections },
    { name: 'merchandise', id: CHECKOUT_MERCHANDISE_CONTAINER_ID, data: merchandiseSections },
    { name: 'loyaltyPurchasePoints', id: CHECKOUT_LOYALTY_PURCHASE_POINTS_CONTAINER_ID, data: loyaltyPurchasePointsSections },
  ]
  const result = new Map<string, any>()

  sections.forEach(({ name, data, id }, index) => {
    if (data.length) {
      result.set(name, { checkoutNumber: index + 1, data, id })
    }
  })

  return {
    review: result.get('review'),
    experienceTransfer: result.get('experienceTransfer'),
    experienceLounge: result.get('experienceLounge'),
    insurance: result.get('insurance'),
    bundledCarHire: result.get('bundledCarHire'),
    travellerDetails: result.get('travellerDetails'),
    arrivalDetails: result.get('arrivalDetails'),
    payment: result.get('payment'),
    merchandise: result.get('merchandise'),
    loyaltyPurchasePoints: result.get('loyaltyPurchasePoints'),
  }
}

const isLuxPlusSubscriptionInFlowCheckout = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => getCheckoutBenefitsView(state),
  (isLuxPlusEnabled, isStandaloneLuxPlusCheckout, canRedeemLuxPlusBenefits, benefitsView): boolean => {
    return isLuxPlusEnabled &&
      !isStandaloneLuxPlusCheckout &&
      !canRedeemLuxPlusBenefits &&
      !!benefitsView.data
  },
)

const getRequiresLoyaltySection = createSelector(
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => getCanRedeemCheckoutLoyaltyBenefits(state),
  (state: App.State) => getHasLoyaltyAccount(state),
  selectIsGuestCheckoutMode,
  (isEnabled, canRedeem, hasAccount, isGuestCheckoutMode): boolean => {
    // Guest checkout is the shortest path to a booking. A membership sign-up
    // prompt asks the customer to take on a second decision mid-purchase, and
    // the account it would attach to is one they have not knowingly created yet.
    if (isGuestCheckoutMode) return false
    // we show benefits if they can redeem, but if they don't have account we show a sign up section
    return isEnabled && (canRedeem || !hasAccount)
  },
)

const isLuxPlusSubscriptionStandaloneCheckout = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (isLuxPlusEnabled, isStandaloneLuxPlusCheckout, canRedeemLuxPlusBenefits): boolean =>
    isLuxPlusEnabled && isStandaloneLuxPlusCheckout && !canRedeemLuxPlusBenefits,
)

const getReviewCheckoutPageSection = createSelector(
  (state: App.State) => getCustomOfferItems(state),
  (state: App.State) => accommodationViews(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => getBundleAndSaveItems(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getCarHireItemsView(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => getGiftCardItems(state),
  (state: App.State) => getPurchaseCreditItems(state),
  (state: App.State) => getTourV2ExperienceItemsViews(state),
  (state: App.State) => isSimpleStandaloneCheckout(state),
  (state: App.State) => isLuxPlusAvailableToAddToCart(state),
  (state: App.State) => isLuxPlusSubscriptionInFlowCheckout(state),
  (state: App.State) => isLuxPlusSubscriptionStandaloneCheckout(state),
  (state: App.State) => getIsOneClickTransferModuleEnabled(state),
  (state: App.State) => getRequiresLoyaltySection(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getFlightLoungePassExperienceAvailable(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  (
    customOfferItems,
    accommodationViews,
    villaViews,
    bundleAndSaveItems,
    checkoutAccommodationOfferView,
    carHireItemsView,
    flightItemsView,
    experienceViews,
    transferViews,
    giftCardItems,
    purchaseCreditItems,
    tourV2ExperienceItemsViews,
    isSimpleStandaloneCheckout,
    isLuxPlusAvailableToAddToCart,
    isLuxPlusSubscriptionInFlowCheckout,
    isLuxPlusSubscriptionStandaloneCheckout,
    oneClickAirportTransfersABTestEnabled,
    hasAnyLoyaltyBenefitsToRedeem,
    postPurchase,
    flightLoungePassExperienceAvailable,
    funnelFeatures,
    isInternationalCheckoutFlowTreatment,
  ): Array<CheckoutSectionsReview> => {
    const shouldShowExperienceUpsells = funnelFeatures.experiences && !isInternationalCheckoutFlowTreatment
    const sections: Array<[CheckoutSectionsReview, boolean]> = [
      ['luxPlusInlineToggle', funnelFeatures.luxPlus && isInternationalCheckoutFlowTreatment && isLuxPlusAvailableToAddToCart],
      ['customOffer', !!customOfferItems.length],
      ['accommodation', !!accommodationViews.length],
    ]
    if (checkoutAccommodationOfferView.data?.some((view: App.Checkout.AccommodationOfferView) => view.isBundled)) {
      sections.unshift(
        ['tour', !!checkoutAccommodationOfferView.data?.length],
        ['tourExperience', shouldShowExperienceUpsells && !!tourV2ExperienceItemsViews.data.length],
      )
    } else {
      sections.push(
        ['tour', !!checkoutAccommodationOfferView.data?.length],
        ['tourExperience', shouldShowExperienceUpsells && !!tourV2ExperienceItemsViews.data.length],
      )
    }
    const hasStandaloneExperience = experienceViews.data.some(view => !view.item.isAccomBundle && !view.item.experiencesPassport && !isCheckoutEsimUpsellExperienceItem(view))

    sections.push(
      ['villa', !!villaViews.data.length],
      ['bundleAndSave', !!bundleAndSaveItems.length],
      ['cruise', !!checkoutAccommodationOfferView.data.filter(isCruiseOfferView)?.length],
      ['carHire', !!carHireItemsView.data.length],
      ['flight', !!flightItemsView.data.length && postPurchase !== 'flight-change-dates'],
      ['flightDateChange', !!flightItemsView.data.length && postPurchase === 'flight-change-dates'],
      ['experience', shouldShowExperienceUpsells && !flightLoungePassExperienceAvailable && hasStandaloneExperience],
      [
        'reviewTransfer',
        (!oneClickAirportTransfersABTestEnabled || config.EXPERIENCE_TRANSFER_DEDICATED_PAGE) &&
        !!transferViews.filter(t => !t.transfer.isHomePickup).length,
      ],
      ['giftCard', !!giftCardItems.length],
      ['purchaseCredit', !!purchaseCreditItems.length],
      ['customerServicePanel', (!!accommodationViews.length || isSimpleStandaloneCheckout) && !config.agentHub.isEnabled && !isLuxPlusSubscriptionStandaloneCheckout],
      ['luxPlusInflow', funnelFeatures.luxPlus && !isInternationalCheckoutFlowTreatment && isLuxPlusSubscriptionInFlowCheckout],
      ['luxPlusReconsider', funnelFeatures.luxPlus && !isInternationalCheckoutFlowTreatment && isLuxPlusSubscriptionInFlowCheckout],
      ['luxPlusStandalone', !isInternationalCheckoutFlowTreatment && isLuxPlusSubscriptionStandaloneCheckout],
      // A cruise passenger amendment is a flat service fee: no loyalty benefits or points apply to it
      ['loyalty', hasAnyLoyaltyBenefitsToRedeem && postPurchase !== 'cruise-amend-passengers'],
      ['charterUpgrade', postPurchase === 'charter-upgrade'],
      ['flightSeats', postPurchase === 'flight-seats'],
      ['flightBaggage', postPurchase === 'flight-baggage'],
      ['cruiseAmendPassengers', postPurchase === 'cruise-amend-passengers'],
    )

    return activeSections(sections)
  },
)

const getExperienceTransferCheckoutPageSection = createSelector(
  (state: App.State) => getOneClickTransferPresentationData(state).oneClickTransfer,
  (state: App.State) => getHasOneClickTransferOptionsWithKnownType(state),
  (state: App.State) => getTransferItems(state, false),
  (state: App.State) => getIsOneClickTransferModuleEnabled(state),
  (state: App.State) => getExperienceOneClickTransferOptions(state),
  (state: App.State) => getUnifiedTransferView(state),
  (state: App.State) => getIsOneClickTransferAvailable(state),
  (state: App.State) => getIsOneClickTransferLoading(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getHasBothTransferDirectionsBundled(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  isGroupPaymentShareCheckout,
  (
    oneClickTransfer,
    hasOptionsWithKnownType,
    checkoutTransferItems,
    oneClickAirportTransfersABTestEnabled,
    onClickTransferOptions,
    unifiedTransferView,
    isAvailableTransfers,
    isTransferLoading,
    funnelFeatures,
    hasBothDirectionsBundled,
    isInternationalCheckoutFlowTreatment,
    isGroupPaymentShare,
  ): Array<CheckoutSectionsExperienceTransfer> => {
    if (
      isGroupPaymentShare ||
      config.EXPERIENCE_TRANSFER_DEDICATED_PAGE ||
      !funnelFeatures.experiences ||
      isInternationalCheckoutFlowTreatment
    ) {
      return activeSections([
        ['experienceTransfer', false],
      ])
    }

    // Hide airport transfers when loyalty transfers are active, but keep the it visible for home-to-airport so users can add more
    const hasLoyaltyTransfers =
      (unifiedTransferView.hasRequiredData &&
        unifiedTransferView.data.transferItems.length > 0 &&
        unifiedTransferView.data.transferType !== 'HOME_TO_AIRPORT')
    if (hasLoyaltyTransfers || hasBothDirectionsBundled) return []

    if (isTransferLoading) return ['experienceTransfer']

    if (!oneClickAirportTransfersABTestEnabled || (!oneClickTransfer && !checkoutTransferItems.length) || !isAvailableTransfers) {
      return activeSections([
        ['experienceTransfer', false],
      ])
    }

    return activeSections([
      ['experienceTransfer', hasOptionsWithKnownType],
    ])
  })

const getExperienceLoungeCheckoutPageSection = createSelector(
  (state: App.State) => getFlightLoungePassExperienceAvailable(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => getIsInternationalCheckoutFlowTreatment(state),
  isGroupPaymentShareCheckout,
  (flightLoungePassExperienceAvailable, funnelFeatures, isInternationalCheckoutFlowTreatment, isGroupPaymentShare): Array<CheckoutSectionsExperienceLounge> => {
    if (isGroupPaymentShare) {
      return []
    }

    return activeSections([
      ['experienceLounge', !isInternationalCheckoutFlowTreatment && funnelFeatures.experiences && flightLoungePassExperienceAvailable],
    ])
  },
)

export function shouldShowPassportCardInsuranceSectionForInputs(
  postPurchase: App.CheckoutCartMode | undefined,
  checkoutItems: Array<App.Checkout.AnyItem>,
): boolean {
  if (!postPurchase) return false

  return checkoutItems.some(isPassportCardInsuranceItem)
}

const getInsuranceCheckoutPageSection = createSelector(
  selectIsGuestCheckoutMode,
  (state: App.State) => getShouldShowSpoofedInsuranceSection(state),
  (state: App.State) => getShouldShowRegularInsuranceSection(state),
  (state: App.State) => getShouldShowBookingProtectionSection(state),
  (state: App.State) => getShouldShowSideBySideInsuranceSection(state),
  (state: App.State) => getFunnelFeatures(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => isPassportCardInsuranceSupportedViaCampaign(state),
  (state: App.State) => isPassportCardInsuranceSupportedForToursCruises(state),
  (state: App.State) => isPassportCardInsuranceSupportedPostPurchase(state),
  isGroupPaymentShareCheckout,
  (isGuestCheckoutMode, showSpoofedInsurance, showRegularInsurance, showBookingProtection, showSideBySideInsurance, funnelFeatures, postPurchase, checkoutItems, showPassportCardViaCampaign, showPassportCardForToursCruises, showPassportCardPostPurchase, isGroupPaymentShare): Array<CheckoutSectionsInsurance> => {
    // Guest checkout currently accepts only a bedbank item, so insurance cannot
    // be included in its public order request.
    if (isGuestCheckoutMode) return []

    if (isGroupPaymentShare) {
      return activeSections([
        ['passportCardInsurance', false],
        ['spoofedInsurance', false],
        ['sideBySideInsurance', false],
        ['insurance', false],
        ['bookingProtection', false],
      ])
    }

    // Spoofed insurance shows both CFMR and travel protection in spoof mode
    // It is gated by both flags - if either is disabled, spoofed section is hidden
    const spoofedEnabled = funnelFeatures.cfmr && funnelFeatures.travelProtection
    // When PassportCard is the chosen product it replaces every other insurance option.
    const showPassportCard = showPassportCardViaCampaign || showPassportCardForToursCruises || showPassportCardPostPurchase
    // The side-by-side insurance experiment presents CFMR and travel protection in one section, so
    // it replaces the separate sections for both products. Its decision already accounts for the
    // funnel toggles and PassportCard, so the same value drives the section title and activation.
    return activeSections([
      ['passportCardInsurance', shouldShowPassportCardInsuranceSectionForInputs(postPurchase, checkoutItems) || showPassportCard],
      ['spoofedInsurance', showSpoofedInsurance && spoofedEnabled && !showPassportCard],
      ['sideBySideInsurance', showSideBySideInsurance],
      ['insurance', showRegularInsurance && funnelFeatures.travelProtection && !showPassportCard && !showSideBySideInsurance],
      ['bookingProtection', showBookingProtection && funnelFeatures.cfmr && !showPassportCard && !showSideBySideInsurance],
    ])
  },
)

const getBundledCarHireCheckoutPageSection = createSelector(
  (state: App.State) => isBundledCarHireEnabled(state),
  (state: App.State) => isCartEligibleForBundledCarHire(state),
  (state: App.State) => getFunnelFeatures(state),
  isGroupPaymentShareCheckout,
  (isBundledCarHireEnabled, isCartEligibleForBundledCarHire, funnelFeatures, isGroupPaymentShare): Array<CheckoutSectionsCarHire> => {
    if (isGroupPaymentShare) {
      return []
    }

    return activeSections([
      ['bundledCarHire', isBundledCarHireEnabled && isCartEligibleForBundledCarHire && funnelFeatures.carHire],
    ])
  },
)

const getTravellerCheckoutPageSection = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => shouldShowTravellerForm(state),
  (state: App.State) => checkoutShouldShowCarHireArrivalInput(state),
  (state: App.State) => state.checkout.cart.mode,
  (state: App.State) => cartIncludesFlights(state),
  (state: App.State) => getFlightItems(state),
  (
    existingOrder,
    shouldShowTravellerForm,
    shouldShowCarHireArrivalInput,
    cartMode,
    hasFlightsInCart,
    flightItems,
  ): Array<CheckoutSectionsTravellerDetails> => {
    // Don't show traveller section at all for payment-only / flight-change / charter-upgrade / cruise amendment modes
    if (['payment-schedule-balance', 'deposit-balance', 'group-payment-share', 'flight-change-dates', 'charter-upgrade', 'flight-seats', 'flight-baggage', 'cruise-amend-passengers'].includes(cartMode ?? '')) {
      return []
    }

    // Only show flight services after final fares are confirmed to prevent race condition with seat fetching API
    // need to check the final fares for all flight items that have flights available as feeder flights can be skipped in-case of charter
    const hasFlightsFinalFares = flightItems?.length > 0
      && flightItems?.filter(item => item.flights.length > 0)
        .every(item => item.hasFinalFare)

    // array of tuples as the order matters
    return activeSections([
      ['travellerDetails', shouldShowTravellerForm],
      ['flightServices', hasFlightsInCart && hasFlightsFinalFares && config.CHECKOUT_FLIGHT_SERVICES_ENABLED],
      ['carHireArrivalFlightNumber', shouldShowCarHireArrivalInput],
      ['agentBookingDetails', config.agentHub.isEnabled],
      ['depositDetailsNotice', !!existingOrder?.depositDetails && isBalancePending(existingOrder.depositDetails.deposit_status)],
    ])
  },
)

const getArrivalDetailsCheckoutPageSection = createSelector(
  (state: App.State) => shouldShowArrivalDetailsForm(state),
  (
    shouldShowArrivalDetailsForm,
  ): Array<CheckoutSectionsArrivalDetails> => {
    return activeSections([
      ['arrivalDetails', shouldShowArrivalDetailsForm],
    ])
  })

const getPaymentCheckoutPageSection = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => shouldShowPaymentForm(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (
    existingOrder,
    showPaymentForm,
    postPurchase,
  ): Array<CheckoutSectionsPayment> => {
    // array of tuples as the order matters
    return activeSections([
      ['instalmentBalancePaymentSchedule', (postPurchase === 'instalment' || postPurchase === 'instalment-balance') && !!existingOrder?.instalmentDetails],
      ['paymentForm', showPaymentForm],
    ])
  },
)

const getMerchandiseCheckoutPageSection = createSelector(
  (state: App.State) => getMerchandiseItems(state),
  (merchandiseItems): Array<CheckoutSectionsMerchandise> => {
    return activeSections([
      ['merchandise', !!merchandiseItems.length],
      ['merchandiseShipping', !!merchandiseItems.length],
    ])
  },
)

const getLoyaltyPurchasePointsCheckoutPageSection = createSelector(
  (state: App.State) => getLoyaltyPurchasePointsItems(state),
  (loyaltyPurchasePointsItems): Array<CheckoutSectionsLoyaltyPurchasePoints> => {
    return activeSections([
      ['loyaltyPurchasePoints', !!loyaltyPurchasePointsItems.length],
    ])
  },
)

const getExperienceTransferSectionTitle = createSelector(
  (state: App.State) => getBundledTransferDirection(state),
  (bundledTransferDirection): CheckoutSectionTitle => {
    if (bundledTransferDirection === 'AIRPORT-TO-HOTEL') {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.experienceTransferToAirport }
    }
    if (bundledTransferDirection === 'HOTEL-TO-AIRPORT') {
      return { kind: 'descriptor', descriptor: sectionTitleMessages.experienceTransferToHotel }
    }
    return { kind: 'descriptor', descriptor: sectionTitleMessages.experienceTransfer }
  },
)

const getPaymentSectionTitle = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => isStandaloneLoyaltyPurchasePoints(state),
  (total, isLoyaltyPurchasePoints): CheckoutSectionTitle | undefined => {
    // UI spacing hack - see related <PaymentForm />
    if (isLoyaltyPurchasePoints) return undefined
    return total > 0 ? { kind: 'descriptor', descriptor: sectionTitleMessages.paymentHow } : undefined
  },
)

export const getCheckoutPageSections = createSelector(
  (state: App.State) => getReviewCheckoutPageSection(state),
  (state: App.State) => getExperienceTransferCheckoutPageSection(state),
  (state: App.State) => getExperienceLoungeCheckoutPageSection(state),
  (state: App.State) => getInsuranceCheckoutPageSection(state),
  (state: App.State) => getBundledCarHireCheckoutPageSection(state),
  (state: App.State) => getTravellerCheckoutPageSection(state),
  (state: App.State) => getArrivalDetailsCheckoutPageSection(state),
  (state: App.State) => getPaymentCheckoutPageSection(state),
  (state: App.State) => getMerchandiseCheckoutPageSection(state),
  (state: App.State) => getLoyaltyPurchasePointsCheckoutPageSection(state),
  (state: App.State) => getPaymentSectionTitle(state),
  (state: App.State) => getReviewSectionTitle(state),
  (state: App.State) => getTravellerSectionTitle(state),
  (state: App.State) => getInsuranceSectionTitle(state),
  (state: App.State) => getBundledCarHireSectionTitle(state),
  (state: App.State) => getExperienceTransferSectionTitle(state),
  (
    reviewSections,
    experienceTransferSection,
    experienceLoungeSection,
    insuranceSections,
    bundledCarHireSection,
    travellerSections,
    arrivalDetails,
    paymentSections,
    merchandiseSections,
    loyaltyPurchasePointsSections,
    paymentSectionTitle,
    reviewSectionTitle,
    travellerSectionTitle,
    insuranceSectionTitle,
    bundledCarHireSectionTitle,
    experienceTransferSectionTitle,
  ): CheckoutSections => {
    const numberedSections = getNumberedSections({
      reviewSections,
      experienceTransferSection,
      experienceLoungeSection,
      insuranceSections,
      bundledCarHireSection,
      travellerSections,
      arrivalDetails,
      paymentSections,
      merchandiseSections,
      loyaltyPurchasePointsSections,
    })

    return {
      review: { title: reviewSectionTitle, ...numberedSections.review },
      experienceTransfer: { title: experienceTransferSectionTitle, ...numberedSections.experienceTransfer },
      experienceLounge: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.experienceLounge }, ...numberedSections.experienceLounge },
      insurance: { title: insuranceSectionTitle, ...numberedSections.insurance },
      bundledCarHire: { title: bundledCarHireSectionTitle, ...numberedSections.bundledCarHire },
      travellerDetails: { title: travellerSectionTitle, ...numberedSections.travellerDetails },
      arrivalDetails: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.arrivalDetails }, ...numberedSections.arrivalDetails },
      payment: { title: paymentSectionTitle, ...numberedSections.payment },
      merchandise: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.merchandiseReview }, ...numberedSections.merchandise },
      loyaltyPurchasePoints: { title: { kind: 'descriptor', descriptor: sectionTitleMessages.loyaltyPurchasePointsReview }, ...numberedSections.loyaltyPurchasePoints },
    }
  },
)

export const getHasRentalInCart = createSelector(
  (state: App.State) => state?.checkout?.cart?.items,
  (items) => items?.some(item => item.itemType === 'villa') ?? false,
)
